// src/shared/utils/formatters.js
// ✅ تنسيق موحد — التاريخ دائماً DD/MM/YYYY أرقام فقط

/**
 * تنسيق الأرقام بفواصل الآلاف
 */
export const formatNumber = (number, decimals = 0) => {
  const num = parseFloat(number);
  if (isNaN(num)) return (0).toFixed(decimals);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// ══════════════════════════════════════════════════════════
//  التاريخ — DD/MM/YYYY أرقام فقط
// ══════════════════════════════════════════════════════════

/**
 * تحويل أي قيمة تاريخ إلى DD/MM/YYYY
 * يقبل: "2025-01-15" | Date | timestamp
 * يرجع: "15/01/2025"
 */
export const formatDate = (value) => {
  if (!value) return '—';
  try {
    // إذا كانت سلسلة بصيغة YYYY-MM-DD نحولها مباشرة بدون Date() لتجنب مشاكل timezone
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  } catch {
    return String(value);
  }
};

/**
 * تنسيق التاريخ والوقت معاً — DD/MM/YYYY HH:MM
 * يقبل: ISO string | Date
 * يرجع: "15/01/2025 09:30"
 */
export const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const d  = String(date.getDate()).padStart(2, '0');
    const m  = String(date.getMonth() + 1).padStart(2, '0');
    const y  = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${d}/${m}/${y} ${hh}:${mm}`;
  } catch {
    return String(value);
  }
};

/**
 * تنسيق الوقت فقط — HH:MM
 * يقبل: "09:30:00" | "09:30" | ISO string
 * يرجع: "09:30"
 */
export const formatTime = (value) => {
  if (!value) return '—';
  // إذا كان بصيغة HH:MM:SS أو HH:MM
  if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  } catch {
    return String(value);
  }
};

/**
 * تنسيق التاريخ النسبي — "اليوم" | "غداً" | "أمس" | DD/MM/YYYY
 * مفيد لعرض تواريخ الاستحقاق
 */
export const formatDateRelative = (value) => {
  if (!value) return '—';
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let date;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(value);
      date.setHours(0, 0, 0, 0);
    }

    if (isNaN(date.getTime())) return String(value);

    const diff = Math.round((date - today) / 86400000);

    if (diff === 0)  return 'اليوم';
    if (diff === 1)  return 'غداً';
    if (diff === -1) return 'أمس';
    if (diff < -1)   return `${formatDate(value)} · متأخر ${Math.abs(diff)} يوم`;
    return formatDate(value);
  } catch {
    return String(value);
  }
};

/**
 * تنسيق محور التاريخ (للـ KanbanBoard) — DD/MM/YYYY مع تمييز
 * يرجع: { text, isLate, isToday }
 */
export const formatDateHeader = (value) => {
  if (!value || value === 'nodate') {
    return { text: 'بدون تاريخ استحقاق', isLate: false, isToday: false };
  }
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let date;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(value);
      date.setHours(0, 0, 0, 0);
    }

    const diff   = Math.round((date - today) / 86400000);
    const base   = formatDate(value);
    const isLate  = diff < 0;
    const isToday = diff === 0;

    let text = base;
    if (diff < 0)  text = `${base}  ·  متأخر ${Math.abs(diff)} يوم`;
    if (diff === 0) text = `${base}  ·  اليوم`;
    if (diff === 1) text = `${base}  ·  غداً`;

    return { text, isLate, isToday };
  } catch {
    return { text: String(value), isLate: false, isToday: false };
  }
};

/**
 * هل التاريخ متأخر؟
 */
export const isDateLate = (value, status) => {
  if (!value || status === 'done' || status === 'cancelled') return false;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let date;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(value);
      date.setHours(0, 0, 0, 0);
    }
    return date < today;
  } catch { return false; }
};

// ══════════════════════════════════════════════════════════
//  Aliases للتوافق مع الكود القديم
// ══════════════════════════════════════════════════════════

/** @deprecated استخدم formatDate بدلاً منه */
export const formatDateArabic = formatDate;

// ══════════════════════════════════════════════════════════
//  ثوابت الترجمة
// ══════════════════════════════════════════════════════════

export const BOOKING_TYPE_LABELS = {
  graduation:        'حفلات التخرج',
  wedding:           'الأعراس',
  kids:              'تصوير أطفال',
  event:             'مناسبات',
  special:           'طلب خاص',
  digital_marketing: 'تسويق إلكتروني',
};

export const BOOKING_STATUS_LABELS = {
  pending:     'قيد الانتظار',
  confirmed:   'مؤكد',
  in_progress: 'قيد التنفيذ',
  completed:   'مكتمل',
  cancelled:   'ملغي',
  active:      'الحجوزات النشطة',
  all:         'كل الحالات',
};

export const TASK_STATUS_LABELS = {
  todo:        'مطلوبة',
  in_progress: 'قيد التنفيذ',
  done:        'مكتملة',
  cancelled:   'ملغاة',
};

export const TASK_PRIORITY_LABELS = {
  high:   'عالية',
  medium: 'متوسطة',
  low:    'منخفضة',
};

/**
 * بناء رسالة خطأ من response.data
 */
export const parseApiError = (err, fallback = 'حدث خطأ غير متوقع.') => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.error)  return data.error;
  return Object.entries(data)
    .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(' ') : m}`)
    .join(' | ');
};